// sourced and extended from https://github.com/ethanselzer/react-hover-observer 

import React, { Children, cloneElement } from 'react'
import { omit } from 'lodash'

type HoverContext = {
  e: React.MouseEvent
  setIsHovering: () => void
  unsetIsHovering: () => void
}

type Props = {
  className?: string
  hoverDelayInMs?: number
  hoverOffDelayInMs?: number
  onHoverChanged?: (state: { isHovering: boolean }) => void
  onMouseEnter?: (ctx: HoverContext) => void
  onMouseLeave?: (ctx: HoverContext) => void
  onMouseOver?: (ctx: HoverContext) => void
  onMouseOut?: (ctx: HoverContext) => void
  shouldDecorateChildren?: boolean
  children?: React.ReactNode | ((props: { isHovering: boolean }) => React.ReactNode)
}

type State = {
  isHovering: boolean
}

export class ReactHoverObserver extends React.Component<Props, State> {
  static displayName = 'ReactHoverObserver'

  static defaultProps = {
    hoverDelayInMs: 0,
    hoverOffDelayInMs: 0,
    onHoverChanged: () => {},
    onMouseEnter: ({ setIsHovering }: HoverContext) => setIsHovering(),
    onMouseLeave: ({ unsetIsHovering }: HoverContext) => unsetIsHovering(),
    onMouseOver: () => {},
    onMouseOut: () => {},
    shouldDecorateChildren: true,
  }

  timerIds: number[] = []

  constructor(props: Props) {
    super(props)
    this.state = { isHovering: false }
  }

  componentWillUnmount() {
    this.clearTimers()
  }

  setIsHovering = () => {
    this.clearTimers()
    const id = window.setTimeout(() => {
      const state = { isHovering: true }
      this.setState(state, () => this.props.onHoverChanged?.(state))
    }, this.props.hoverDelayInMs)
    this.timerIds.push(id)
  }

  unsetIsHovering = () => {
    this.clearTimers()
    const id = window.setTimeout(() => {
      const state = { isHovering: false }
      this.setState(state, () => this.props.onHoverChanged?.(state))
    }, this.props.hoverOffDelayInMs)
    this.timerIds.push(id)
  }

  clearTimers() {
    this.timerIds.forEach(clearTimeout)
    this.timerIds = []
  }

  onMouseEnter = (e: React.MouseEvent) => {
    this.props.onMouseEnter?.({ e, setIsHovering: this.setIsHovering, unsetIsHovering: this.unsetIsHovering })
  }

  onMouseLeave = (e: React.MouseEvent) => {
    this.props.onMouseLeave?.({ e, setIsHovering: this.setIsHovering, unsetIsHovering: this.unsetIsHovering })
  }

  onMouseOver = (e: React.MouseEvent) => {
    this.props.onMouseOver?.({ e, setIsHovering: this.setIsHovering, unsetIsHovering: this.unsetIsHovering })
  }

  onMouseOut = (e: React.MouseEvent) => {
    this.props.onMouseOut?.({ e, setIsHovering: this.setIsHovering, unsetIsHovering: this.unsetIsHovering })
  }

  getIsReactComponent(child: React.ReactElement) {
    return typeof child.type === 'function'
  }

  shouldDecorateChild(child: any): boolean {
    return !!child && this.getIsReactComponent(child) && this.props.shouldDecorateChildren
  }

  decorateChild(child: React.ReactElement, props: any) {
    return cloneElement(child, props)
  }

  renderChildrenWithProps(children: Props['children'], props: any) {
    if (typeof children === 'function') {
      return children({ isHovering: this.state.isHovering })
    }

    return Children.map(children as React.ReactElement[], (child) =>
      this.shouldDecorateChild(child) ? this.decorateChild(child, props) : child
    )
  }

  render() {
    const { children, className } = this.props
    const { isHovering } = this.state

    const passthroughProps = omit(this.props, [
      'children',
      'className',
      'hoverDelayInMs',
      'hoverOffDelayInMs',
      'onHoverChanged',
      'onMouseEnter',
      'onMouseLeave',
      'onMouseOver',
      'onMouseOut',
      'shouldDecorateChildren',
    ])

    return (
      <div
        className={className}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
      >
        {this.renderChildrenWithProps(children, { isHovering, ...passthroughProps })}
      </div>
    )
  }
}